/**
 * When something happened, at the precision that tells it from its neighbors.
 *
 * Three tiers, each dropping what the reader can already infer: today needs
 * only a clock, this year needs only a day and a month, and anything older
 * needs the year. So a TIME means today and a DATE means it is not — the two
 * shapes never collide, and a reader reads the smallest thing that
 * distinguishes one entry from the one above it.
 *
 * **The comparison is the local calendar day, not elapsed hours.** Something
 * said at eleven last night is yesterday however few hours ago that was, which
 * is what somebody means by "today" — an elapsed-hours test would print a clock
 * time for it and say it happened this morning.
 *
 * `now` is a parameter so the tiers can be tested against fixed dates rather
 * than against whenever the suite happens to run.
 */
export function when(at: string | Date | null, now: Date = new Date()): string {
  if (!at) return ''
  const d = at instanceof Date ? at : new Date(at)
  if (Number.isNaN(d.getTime())) return ''

  if (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  ) {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    /* The year is context rather than the thing being read, so it appears only
       where the reader cannot assume it. */
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  })
}
